import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Box,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  AccountBalance as AccountBalanceIcon,
  Build as BuildIcon,
  Rule as RuleIcon,
  Assessment as AssessmentIcon,
  Logout as LogoutIcon,
  Settings as SettingsIcon,
  Person as PersonIcon,
  Extension as ExtensionIcon,
  AccountTree as WorkflowIcon,
  Web as WebIcon,
  Folder as FolderIcon,
  School as SchoolIcon,
  Business as BusinessIcon,
  Description as DocumentIcon,
  Assignment as TaskIcon,
  ManageAccounts as ManageAccountsIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  ViewModule as ComponentIcon,
} from '@mui/icons-material';
import { useAuth } from '@/contexts/AuthContext';

const drawerWidth = 280;
const collapsedDrawerWidth = 72;

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const { user, signOut } = useAuth();

  const [mobileOpen, setMobileOpen] = useState(false);
  const [drawerCollapsed, setDrawerCollapsed] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleDrawerCollapse = () => {
    setDrawerCollapsed(!drawerCollapsed);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleSignOut = async () => {
    handleMenuClose();
    await signOut();
  };

  const menuItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' },
    { text: 'Clients', icon: <PeopleIcon />, path: '/clients', roles: ['admin', 'manager'] },
    { text: 'Loans', icon: <AccountBalanceIcon />, path: '/loans', roles: ['admin', 'manager'] },
    { text: 'Documents', icon: <DocumentIcon />, path: '/documents', roles: ['admin', 'manager'] },
    { text: 'Files', icon: <FolderIcon />, path: '/files', roles: ['admin', 'manager', 'analyst'] },
    { text: 'Task Manager', icon: <TaskIcon />, path: '/tasks', roles: ['admin', 'manager', 'analyst'] },
    { text: 'Reporting', icon: <AssessmentIcon />, path: '/reporting', roles: ['admin', 'manager', 'analyst'] },
    { text: 'Training', icon: <SchoolIcon />, path: '/training' },
    { text: 'Apps', icon: <ExtensionIcon />, path: '/apps', roles: ['admin', 'manager'] },
    { text: 'Admin', roles: ['admin', 'manager'] },
    { text: 'User Management', icon: <ManageAccountsIcon />, path: '/users', roles: ['admin', 'manager'] },
    { text: 'Company Setup', icon: <BusinessIcon />, path: '/company/config', roles: ['admin'] },
    { text: 'Form Builder', icon: <BuildIcon />, path: '/form-builder', roles: ['admin', 'manager'] },
    { text: 'Page Builder', icon: <WebIcon />, path: '/page-builder', roles: ['admin', 'manager'] },
    { text: 'Component Builder', icon: <ComponentIcon />, path: '/component-builder', roles: ['admin', 'manager'] },
    { text: 'Rule Builder', icon: <RuleIcon />, path: '/rule-builder', roles: ['admin', 'manager'] },
    { text: 'Workflows', icon: <WorkflowIcon />, path: '/workflows', roles: ['admin', 'manager'] },
    { text: 'Workflow Builder', icon: <WorkflowIcon />, path: '/workflows/builder/new', roles: ['admin', 'manager'] },
  ];

  const filteredMenuItems = menuItems.filter(item => 
    !item.roles || item.roles.includes(user?.role || 'viewer')
  );

  const currentDrawerWidth = drawerCollapsed ? collapsedDrawerWidth : drawerWidth;

  const drawer = (
    <Box>
      <Toolbar sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {!drawerCollapsed && (
          <Box
            component="img"
            src="/iBusiness_logo.svg"
            alt="iBusiness Logo"
            sx={{
              height: "2rem",
            }}
          />
        )}
        <IconButton
          onClick={handleDrawerCollapse}
          sx={{ 
            display: { xs: 'none', md: 'flex' },
            ml: drawerCollapsed ? 0 : 'auto'
          }}
          aria-label="toggle drawer"
        >
          {drawerCollapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
        </IconButton>
      </Toolbar>
      <Divider />
      <List>
        {filteredMenuItems.map((item) => (
          (!item.path) ? 
            <ListItem key={item.text} sx={{ display: drawerCollapsed ? 'none' : 'flex' }}>
              <ListItemText primary={item.text} />
            </ListItem> :
          <ListItem key={item.text} disablePadding>
            <ListItemButton
              selected={location.pathname === item.path}
              onClick={() => {
                navigate(item.path);
                if (isMobile) {
                  setMobileOpen(false);
                }
              }}
              sx={{
                minHeight: 48,
                justifyContent: drawerCollapsed ? 'center' : 'initial',
                px: 2.5,
              }}
            >
              <ListItemIcon 
                sx={{ 
                  color: 'inherit',
                  minWidth: 0,
                  mr: drawerCollapsed ? 0 : 3,
                  justifyContent: 'center',
                }}
              >
                {item.icon}
              </ListItemIcon>
              {!drawerCollapsed && <ListItemText primary={item.text} />}
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { md: `calc(100% - ${currentDrawerWidth}px)` },
          ml: { md: `${currentDrawerWidth}px` },
          transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.standard,
          }),
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            {filteredMenuItems.find(item => item.path === location.pathname)?.text || 'iBusiness'}
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body2" sx={{ display: { xs: 'none', sm: 'block' } }}>
              {user?.firstName} {user?.lastName}
            </Typography>
            <IconButton
              size="large"
              aria-label="account of current user"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleMenuOpen}
              color="inherit"
            >
              <Avatar sx={{ width: 32, height: 32, bgcolor: 'secondary.main' }}>
                {user?.firstName?.charAt(0)}{user?.lastName?.charAt(0)}
              </Avatar>
            </IconButton>
          </Box>

          <Menu
            id="menu-appbar"
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            keepMounted
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
          >
            <MenuItem onClick={handleMenuClose}>
              <ListItemIcon>
                <PersonIcon fontSize="small" />
              </ListItemIcon>
              Profile
            </MenuItem>
            <MenuItem onClick={() => {
              handleMenuClose();
              navigate('/settings');
            }}>
              <ListItemIcon>
                <SettingsIcon fontSize="small" />
              </ListItemIcon>
              Settings
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleSignOut}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              Sign Out
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Box
        component="nav"
        sx={{ width: { md: currentDrawerWidth }, flexShrink: { md: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: currentDrawerWidth,
              transition: theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.standard,
              }),
              overflowX: 'hidden',
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { md: `calc(100% - ${currentDrawerWidth}px)` },
          minHeight: '100vh',
          bgcolor: 'background.default',
          transition: theme.transitions.create(['width'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.standard,
          }),
        }}
      >
        <Toolbar />
        {children}
      </Box>
    </Box>
  );
};

export default Layout;
